import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { appLogout, injectSlice } from '@betty/toolkit/state';
import { GamingHistoryState } from './gamingHistory.types';
import { FetchHistoryParams } from '../shared/history.types';
import { isAfterTimestamp } from '@betty/toolkit/utils';
import { DEFAULT_PAGE_SIZE } from '../shared/history.constants';
import HistoryService from '../../api/HistoryService';

export const fetchGamingHistory = createAsyncThunk(
  'gamingHistory/fetch',
  async ({ startDate, pageNumber = 1, replace }: FetchHistoryParams) => {
    const response = await HistoryService.getGamingHistory({
      params: {
        pageNumber: replace ? 1 : pageNumber,
        startDate,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    });

    return response;
  },
);

const initialState: GamingHistoryState = {
  data: undefined,
  dataState: 'idle',
  lastTimestamp: undefined,
};

const gamingHistory = createSlice({
  name: 'gamingHistory',
  initialState,
  reducers: {
    resetGamingLastTimestamp(state) {
      state.lastTimestamp = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGamingHistory.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(fetchGamingHistory.fulfilled, (state, action) => {
      const { replace, startDate } = action.meta.arg;
      state.dataState = 'fulfilled';
      state.lastTimestamp = startDate;
      if (replace) {
        state.data = action.payload;
      } else {
        const oldItems = state.data?.items;
        const newItems = action.payload.items;
        state.data = action.payload;
        if (oldItems) {
          state.data.items = [...oldItems, ...newItems];
        }
      }
    });
    builder.addCase(fetchGamingHistory.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

const withGamingHistory = injectSlice({
  reducerPath: 'gamingHistory',
  reducer: gamingHistory.reducer,
});

const selectAllItems = withGamingHistory.selector(
  (state) => state.gamingHistory.data?.items,
);

export const selectGamingHistoryLastTimestamp = withGamingHistory.selector(
  (state) => state.gamingHistory.lastTimestamp,
);

export const selectGamingHistoryItems = createSelector(
  [selectAllItems, (_, timestamp) => timestamp],
  (allItems, timestamp) =>
    (allItems ?? []).filter((item) =>
      isAfterTimestamp(item.createdAt, timestamp),
    ),
);

export const selectGamingHistoryDataState = withGamingHistory.selector(
  (state) => state.gamingHistory.dataState,
);

export const selectGamingHistoryHasNextPage = withGamingHistory.selector(
  (state) => state.gamingHistory.data?.hasNextPage,
);

export const selectGamingHistoryPageNumber = withGamingHistory.selector(
  (state) => state.gamingHistory.data?.pageNumber,
);

export const { resetGamingLastTimestamp } = gamingHistory.actions;

export default gamingHistory.reducer;
declare global {
  interface LazyLoadedSlices {
    gamingHistory: ReturnType<typeof gamingHistory.reducer>;
  }
}
